import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CADGridBackground } from "@/components/CADGridBackground";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { useAuthStore } from "@/store/authStore";
import { timing } from "@/theme/tokens";

const REVEAL_EASE = [0.22, 1, 0.36, 1] as const;

export default function SplashScreen() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const resolveAuth = useAuthStore((s) => s.resolveAuth);
  const [minElapsed, setMinElapsed] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    resolveAuth();
    const t = window.setTimeout(() => setMinElapsed(true), timing.splashMinMs);
    return () => window.clearTimeout(t);
  }, [resolveAuth]);

  useEffect(() => {
    if (!minElapsed || status === "resolving") return;
    setLeaving(true);
    const t = window.setTimeout(
      () => navigate("/login", { replace: true }),
      timing.splashExitMs,
    );
    return () => window.clearTimeout(t);
  }, [minElapsed, status, navigate]);

  return (
    <motion.div
      data-testid="splash-screen"
      initial={false}
      animate={{ opacity: leaving ? 0 : 1 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="fixed inset-0 flex items-center justify-center overflow-hidden bg-obsidian"
    >
      <CADGridBackground opacity={0.5} />

      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.06] blur-3xl"
      />

      <div className="relative flex flex-col items-center gap-10 px-8">
        <motion.div
          data-testid="splash-logo"
          initial={{ opacity: 0, scale: 0.96, clipPath: "inset(0 100% 0 0)" }}
          animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0% 0 0)" }}
          transition={{ duration: 1.1, ease: REVEAL_EASE }}
        >
          <BuildEaseLogo className="h-[72px] sm:h-[104px] md:h-[136px]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="h-px w-40 origin-left bg-gradient-to-r from-transparent via-cad/60 to-transparent"
        />
      </div>
    </motion.div>
  );
}
